import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./ImportTransaction2.module.css";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import {
    ChevronLeft,
    ChevronRight,
    // Upload,
    // FileText,
    // CheckCircle,
    XCircle,
    // Clock,
    ChevronDown,
    // Info,
    Download,
    // Eye,
    // MapPin,
    // Save,
    AlertCircle,
    MoreVertical,
    Plus,
    HelpCircle
} from "lucide-react";
import CheckIcon from "../../Assets/mark.png";

const ImportTransactions = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(2);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [selectedFile, setSelectedFile] = useState(null);
    const [dataSource, setDataSource] = useState("");
    const [encoding, setEncoding] = useState("UTF-8");
    const [headerRow, setHeaderRow] = useState(false);
    const [skipEmptyRows, setSkipEmptyRows] = useState(false);

    // State for map column selections
    const [columnMappings, setColumnMappings] = useState({
        "Transaction Date *": "Date",
        "Value Date *": "Value Date",
        "Description / Narration *": "Narration",
        "Reference / Narration 2 *": "Reference",
        "Debit Amount": "Debit",
        "Credit Amount": "Credit",
        "Balance": "Balance",
        "Transaction Type": "Type"
    });

    const steps = [
        {
            id: 1,
            label: "Upload File",
            description: "File uploaded",
            completed: currentStep > 1,
            active: currentStep === 1
        },
        {
            id: 2,
            label: "Map Columns",
            description: "Columns mapped",
            completed: currentStep > 2,
            active: currentStep === 2
        },
        {
            id: 3,
            label: "Preview Data",
            description: "Review and validate data",
            completed: currentStep > 3,
            active: currentStep === 3
        },
        {
            id: 4,
            label: "Import Summary",
            description: "Complete the import",
            completed: currentStep > 4,
            active: currentStep === 4
        }
    ];

    // Available columns for dropdown
    const availableColumns = [
        "Date",
        "Value Date",
        "Narration",
        "Reference",
        "Debit",
        "Credit",
        "Balance",
        "Type",
        "Transaction ID",
        "Account Number",
        "Bank Name",
        "Branch Code"
    ];

    // Generate 100+ rows of transaction data
    const generateTransactionData = () => {
        const data = [];
        const statuses = ["-", "50,000.00", "100,000.00", "10,000.00", "900.00"];
        const customers = [
            "Payment from Customer A",
            "Transfer to Supplier B",
            "Customer Payment",
            "Payroll Run",
            "Payment to Vendor C",
            "Bank Charges",
            "Interest Received",
            "Standing Order",
            "Refund to Customer",
            "POS Purchase",
            "Salary Payment",
            "Utility Bill",
            "Rent Payment",
            "Supplier Payment",
            "Insurance Premium",
            "Loan Repayment",
            "Investment Return",
            "Dividend Payment",
            "Consulting Fee",
            "Software License",
            "Marketing Expense",
            "Travel Reimbursement",
            "Office Supplies",
            "Maintenance Cost"
        ];

        for (let i = 1; i <= 105; i++) {
            const statusIndex = i % statuses.length;
            const customerIndex = i % customers.length;
            const randomCredit = (Math.random() * 100000 + 10000).toFixed(2);
            const randomBalance = (Math.random() * 2000000 + 100000).toFixed(2);
            
            data.push({
                referenceId: `TRF/20260814/${String(i).padStart(3, '0')}`,
                date: `${String(14 - (i % 7)).padStart(2, '0')}-08-26`,
                customer: customers[customerIndex],
                status: statuses[statusIndex],
                credit: i % 3 === 0 ? randomCredit : "-",
                balance: randomBalance,
                valueDate: `${String(14 - (i % 7)).padStart(2, '0')}-08-2026`
            });
        }
        return data;
    };

    const transactionData = generateTransactionData();
    const totalTransactions = transactionData.length;
    const totalPages = Math.ceil(totalTransactions / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalTransactions);
    const currentTransactions = transactionData.slice(startIndex, endIndex);

    const mappingData = [
        { system: "Transaction Date *", column: "Date", sample: "14-08-2026", status: "matched" },
        { system: "Value Date *", column: "Value Date", sample: "14-08-2026", status: "matched" },
        { system: "Description / Narration *", column: "Narration", sample: "Payment from Customer A", status: "matched" },
        { system: "Reference / Narration 2 *", column: "Reference", sample: "14-08-2026", status: "matched" },
        { system: "Debit Amount", column: "Debit", sample: "-", status: "partial" },
        { system: "Credit Amount", column: "Credit", sample: "25,000.00", status: "matched" },
        { system: "Balance", column: "Balance", sample: "450,320.00", status: "unmatched" },
        { system: "Transaction Type", column: "Type", sample: "Credit", status: "matched" },
    ];

    const notes = [
        { icon: <XCircle size={16} className={classes.noteIconError} />, text: "Record not found" },
        { icon: <AlertCircle size={16} className={classes.noteIconWarning} />, text: "Record not updated" },
        { icon: <AlertCircle size={16} className={classes.noteIconWarning} />, text: "Record not deleted" },
    ];

    const handleFileDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleBackClick = () => {
        navigate("/ImportTransaction1");
    };

    const handleNextClick = () => {
        navigate("/ImportTransactions");
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleMappingChange = (systemField, value) => {
        setColumnMappings({
            ...columnMappings,
            [systemField]: value
        });
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            
            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);
            
            if (currentPage <= 3) {
                end = 4;
            }
            if (currentPage >= totalPages - 2) {
                start = totalPages - 3;
            }
            
            if (start > 2) {
                pages.push('...');
            }
            
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            
            if (end < totalPages - 1) {
                pages.push('...');
            }
            
            pages.push(totalPages);
        }
        
        return pages;
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case "matched":
                return <span className={classes.statusMatched}>Matched</span>;
            case "partial":
                return <span className={classes.statusPartial}>Partial</span>;
            case "unmatched":
                return <span className={classes.statusUnmatched}>Unmatched</span>;
            default:
                return null;
        }
    };

    return (
        <div className={classes.page}>
            <Header />
            <Sidebar />

            <main className={classes.main}>
                <div className={classes.container}>
                    {/* Header Section */}
                    <div className={classes.headerSection}>
                        <div>
                            <h1 className={classes.title}>Import Transactions</h1>
                            <p className={classes.subtitle}>Import bank statement or transaction file into the system.</p>
                        </div>
                    </div>

                    {/* Step Indicator */}
                    <div className={classes.stepsContainer}>
                        {steps.map((step, index) => (
                            <React.Fragment key={step.id}>
                                <div className={classes.stepWrapper}>
                                    <div className={`${classes.stepCircle} ${step.completed ? classes.stepCompleted : ''} ${step.active ? classes.stepActive : ''}`}>
                                        {step.completed ? (
                                            <img src={CheckIcon} alt="completed" className={classes.stepIcon} />
                                        ) : (
                                            <span className={classes.stepNumber}>{step.id}</span>
                                        )}
                                    </div>
                                    <div className={classes.stepContent}>
                                        <div className={`${classes.stepLabel} ${step.active ? classes.stepLabelActive : ''} ${step.completed ? classes.stepLabelCompleted : ''}`}>
                                            {step.id}. {step.label}
                                        </div>
                                        <div className={classes.stepDescription}>{step.description}</div>
                                    </div>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`${classes.stepLine} ${step.completed ? classes.stepLineCompleted : ''}`}></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Transaction Table */}
                    <div className={classes.customerTableSection}>
                        <div className={classes.tableWrapper}>
                            <div className={classes.tableContainer}>
                                <table className={classes.table}>
                                    <thead>
                                        <tr>
                                            <th>Reference / Date</th>
                                            <th>Narration</th>
                                            <th>Debit (₦)</th>
                                            <th>Credit (₦)</th>
                                            <th>Balance (₦)</th>
                                            <th>Value Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentTransactions.map((item, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className={classes.reference}>{item.referenceId}</div>
                                                    <div className={classes.date}>{item.date}</div>
                                                </td>
                                                <td className={classes.narration}>{item.customer}</td>
                                                <td className={item.status === "-" ? classes.emptyValue : classes.debitValue}>
                                                    {item.status === "-" ? "-" : item.status}
                                                </td>
                                                <td className={item.credit === "-" ? classes.emptyValue : classes.creditValue}>
                                                    {item.credit}
                                                </td>
                                                <td className={classes.balanceValue}>{item.balance}</td>
                                                <td className={classes.valueDate}>{item.valueDate}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Table Footer */}
                        <div className={classes.tableFooter}>
                            <span className={classes.rowsInfo}>
                                Showing {startIndex + 1} to {endIndex} of {totalTransactions} rows
                            </span>
                            <button className={classes.downloadBtn}>
                                <Download size={16} />
                                Download Sample
                            </button>
                        </div>

                        {/* Pagination */}
                        <div className={classes.pagination}>
                            <div className={classes.paginationControls}>
                                <button 
                                    className={classes.pageButton} 
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft size={16} />
                                    Previous
                                </button>
                                {getPageNumbers().map((page, index) => (
                                    <button
                                        key={index}
                                        className={`${classes.pageButton} ${page === currentPage ? classes.activePage : ''}`}
                                        onClick={() => typeof page === 'number' && handlePageChange(page)}
                                        disabled={page === '...'}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button 
                                    className={classes.pageButton} 
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Map Columns Section - With Dropdowns */}
                    <div className={classes.mapColumnsSection}>
                        <h3 className={classes.sectionTitle}>Map Columns</h3>
                        <p className={classes.mapSubtitle}>Map the columns in your file to the appropriate field in the system</p>

                        <div className={classes.mapColumnsTableWrapper}>
                            <table className={classes.mapTable}>
                                <thead>
                                    <tr>
                                        <th>System Field</th>
                                        <th>Column from field</th>
                                        <th>Sample Data</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mappingData.map((item, index) => (
                                        <tr key={index}>
                                            <td className={classes.systemField}>{item.system}</td>
                                            <td>
                                                <div className={classes.selectWrapper}>
                                                    <select 
                                                        className={classes.mapSelect}
                                                        value={columnMappings[item.system] || item.column}
                                                        onChange={(e) => handleMappingChange(item.system, e.target.value)}
                                                    >
                                                        {availableColumns.map((col) => (
                                                            <option key={col} value={col}>{col}</option>
                                                        ))}
                                                    </select>
                                                    <ChevronDown size={16} className={classes.selectIcon} />
                                                </div>
                                            </td>
                                            <td>{item.sample}</td>
                                            <td>{getStatusBadge(item.status)}</td>
                                            <td>
                                                <button className={classes.actionMoreBtn}>
                                                    <MoreVertical size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Map Summary */}
                        <div className={classes.mapSummary}>
                            <div className={classes.mapSummaryLeft}>
                                <span className={classes.mapSummaryCount}>8 out of 8 fields mapped</span>
                                <span className={classes.mapSummaryHint}>Required fields marked with * must be mapped to proceed</span>
                            </div>
                            <div className={classes.mapSummaryRight}>
                                <HelpCircle size={16} className={classes.mapSummaryIcon} />
                                <span className={classes.mapSummaryHelp}>Couldn't find a column? Add a static value to a field.</span>
                            </div>
                        </div>

                        {/* Status Legend - 2 Columns */}
                        <div className={classes.statusLegendWrapper}>
                            <div className={classes.statusLegend}>
                                <div className={classes.legendColumn}>
                                    <div className={classes.legendItem}>
                                        <span className={classes.legendDot} style={{ backgroundColor: "#22c55e" }}></span>
                                        <span className={classes.legendText}>Matched</span>
                                    </div>
                                    <div className={classes.legendItem}>
                                        <span className={classes.legendDot} style={{ backgroundColor: "#f59e0b" }}></span>
                                        <span className={classes.legendText}>Partial</span>
                                    </div>
                                    <div className={classes.legendItem}>
                                        <span className={classes.legendDot} style={{ backgroundColor: "#ef4444" }}></span>
                                        <span className={classes.legendText}>Unmapped</span>
                                    </div>
                                </div>
                                <div className={classes.legendColumn}>
                                    <div className={classes.legendDescItem}>
                                        <span className={classes.legendDesc}>Column has been mapped</span>
                                    </div>
                                    <div className={classes.legendDescItem}>
                                        <span className={classes.legendDesc}>Column mapped but sample data format may vary</span>
                                    </div>
                                    <div className={classes.legendDescItem}>
                                        <span className={classes.legendDesc}>Column not mapped</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Add Static Value Button */}
                        <div className={classes.addStaticSection}>
                            <button className={classes.addStaticBtn}>
                                <Plus size={16} />
                                + Add Static Value
                            </button>
                        </div>
                    </div>

          

                    {/* Navigation Buttons */}
                    <div className={classes.navigationButtons}>
                        <button 
                            className={classes.backButton}
                            onClick={handleBackClick}
                        >
                            <ChevronLeft size={18} />
                            Back: Upload File
                        </button>
                        <button 
                            className={classes.nextButton}
                            onClick={handleNextClick}
                        >
                            Next: Preview Data
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ImportTransactions;